import {
  ReactNode,
  useMemo,
  useState,
  useContext,
  createContext,
  Fragment,
} from "react";
import type { Root, RootContent, PhrasingContent, Code } from "mdast";
import { processor } from "./parse.js";
import { assetSrc, dirOf } from "./assetUrl.js";
import { parseFenceInfo } from "./parseFence.js";
import {
  isDirective,
  directiveAttrs,
  type DirectiveNode,
} from "./parseDirective.js";
import { vizRegistry } from "../viz/registry.js";
import { EmbedContext, EmbedFrame } from "./EmbedFrame.js";
import { Sidebyside } from "./Sidebyside.js";


// Directory of the page being rendered, so relative image links resolve. The
// on-disk markdown keeps relative `./assets/…` links (portable to GitHub);
// in-app we rewrite them to the /api/blob route that streams the repo file.
const MarkdownDirContext = createContext("");
// The repo whose `/api/r/<repo>/blob` route serves this page's relative images.
const MarkdownRepoContext = createContext("");

function MdImage({
  url,
  alt,
  title,
}: {
  url: string;
  alt: string;
  title?: string;
}) {
  const baseDir = useContext(MarkdownDirContext);
  const repo = useContext(MarkdownRepoContext);
  return <img src={assetSrc(url, baseDir, repo)} alt={alt} title={title} />;
}

/**
 * A paragraph or heading on the rendered page. Double-clicking prose opens the
 * editor — the gesture that used to flip the block to `contentEditable` in
 * place (the old `InlineEdit.tsx`). That mode is gone: hand-rolling DOM →
 * markdown meant a whitelist of what was safe to convert, and any block
 * outside it silently wasn't editable. The block editor edits the same prose
 * as raw markdown with no whitelist and no conversion, so the gesture now just
 * takes you there.
 */
function ProseBlock({
  tag: Tag,
  id,
  children,
}: {
  tag: "p" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  id?: string;
  children: ReactNode;
}) {
  const ctx = useContext(EmbedContext);
  if (!ctx.interactive || !ctx.onRequestEdit) return <Tag id={id}>{children}</Tag>;
  return (
    <Tag id={id} className="dw-prose-editable" onDoubleClick={ctx.onRequestEdit}>
      {children}
    </Tag>
  );
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

function innerText(nodes: PhrasingContent[] | RootContent[]): string {
  let out = "";
  for (const n of nodes as PhrasingContent[]) {
    if ("value" in n && typeof n.value === "string") out += n.value;
    else if ("children" in n && Array.isArray(n.children))
      out += innerText(n.children as PhrasingContent[]);
  }
  return out;
}

function renderChildren(
  nodes: RootContent[] | PhrasingContent[],
): ReactNode {
  return nodes.map((node, i) => (
    <Fragment key={i}>{renderNode(node)}</Fragment>
  ));
}

function renderDirective(node: DirectiveNode): ReactNode {
  if (node.type === "containerDirective" && node.name === "sidebyside") {
    const columns = node.children.map((child, i) => (
      <div className="dw-sidebyside-col" key={i}>
        {renderNode(child)}
      </div>
    ));
    return (
      <Sidebyside node={node} attrs={directiveAttrs(node)} columns={columns} />
    );
  }
  // Unknown directive name: render its children as a plain block, so adding a
  // new directive later (`:::note`, `:::filters`, …) degrades gracefully.
  return <>{renderChildren(node.children)}</>;
}

function renderNode(node: RootContent | PhrasingContent): ReactNode {
  if (isDirective(node as { type: string })) {
    return renderDirective(node as unknown as DirectiveNode);
  }
  switch (node.type) {
    // Prose blocks carry the double-click-to-edit gesture (ProseBlock above).
    case "paragraph":
      return <ProseBlock tag="p">{renderChildren(node.children)}</ProseBlock>;
    case "heading": {
      const text = innerText(node.children);
      const id = slugify(text) || undefined;
      return (
        <ProseBlock tag={`h${node.depth}` as "h1"} id={id}>
          {renderChildren(node.children)}
        </ProseBlock>
      );
    }
    case "text":
      return node.value;
    case "emphasis":
      return <em>{renderChildren(node.children)}</em>;
    case "strong":
      return <strong>{renderChildren(node.children)}</strong>;
    case "delete":
      return <del>{renderChildren(node.children)}</del>;
    case "inlineCode":
      return <code>{node.value}</code>;
    case "break":
      return <br />;
    case "thematicBreak":
      return <hr />;
    case "blockquote":
      return <blockquote>{renderChildren(node.children)}</blockquote>;
    case "link":
      return (
        <a href={node.url} title={node.title ?? undefined}>
          {renderChildren(node.children)}
        </a>
      );
    case "image":
      return (
        <MdImage url={node.url} alt={node.alt ?? ""} title={node.title ?? undefined} />
      );
    case "list":
      return node.ordered ? (
        <ol start={node.start ?? undefined}>{renderChildren(node.children)}</ol>
      ) : (
        <ul>{renderChildren(node.children)}</ul>
      );
    case "listItem":
      return <li>{renderChildren(node.children)}</li>;
    case "table": {
      const [head, ...body] = node.children;
      return (
        <table>
          {head && (
            <thead>
              <tr>
                {head.children.map((cell, i) => (
                  <th key={i}>{renderChildren(cell.children)}</th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {body.map((row, i) => (
              <tr key={i}>
                {row.children.map((cell, j) => (
                  <td key={j}>{renderChildren(cell.children)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    case "code": {
      // Remark splits "```metric label=foo" into `lang` (the first word) and
      // `meta` (everything after). Reassemble so parseFenceInfo sees the lot.
      const info = [node.lang ?? "", node.meta ?? ""]
        .filter(Boolean)
        .join(" ");
      const { kind, attrs } = parseFenceInfo(info);
      const entry = vizRegistry[kind];
      if (entry) {
        return (
          <EmbedFrame
            node={node as Code}
            kind={kind}
            attrs={attrs}
            source={node.value}
            View={entry.view}
            editor={entry.editor}
            label={entry.label}
          />
        );
      }
      return (
        <pre>
          <code className={node.lang ? `language-${node.lang}` : undefined}>
            {node.value}
          </code>
        </pre>
      );
    }
    case "html":
      // Untrusted markdown -> render as plain text rather than raw HTML.
      return <span className="dw-raw-html">{node.value}</span>;
    default:
      if ("children" in node && Array.isArray(node.children))
        return renderChildren(node.children as RootContent[]);
      return null;
  }
}

export function Markdown({
  source,
  onSourceChange,
  path,
  repo = "",
  onRequestEdit,
}: {
  source: string;
  /** When provided, embeds become selectable/editable and edits splice back
   * through here. Omit it for a plain, read-only render. */
  onSourceChange?: (next: string) => void;
  /** Repo path of the page, so relative image links resolve to /api/blob. */
  path?: string;
  /** Content repo whose `/api/r/<repo>/blob` route serves relative images. */
  repo?: string;
  /** Open the page editor — wired to double-click on prose. */
  onRequestEdit?: () => void;
}) {
  const { tree, nodeIndex } = useMemo(() => {
    const t = processor.parse(source) as Root;
    // Number selectable nodes (viz embeds + `:::sidebyside` directives) in
    // document order so selection survives a re-parse (offsets shift when
    // content above changes, indices don't).
    const map = new Map<object, number>();
    let i = 0;
    const walk = (n: RootContent): void => {
      if (n.type === "code") {
        const info = [n.lang ?? "", n.meta ?? ""].filter(Boolean).join(" ");
        if (vizRegistry[parseFenceInfo(info).kind]) map.set(n, i++);
      } else if (isDirective(n as { type: string })) {
        const d = n as unknown as DirectiveNode;
        if (d.type === "containerDirective" && d.name === "sidebyside")
          map.set(n, i++);
      }
      if ("children" in n && Array.isArray(n.children))
        (n.children as RootContent[]).forEach(walk);
    };
    t.children.forEach(walk);
    return { tree: t, nodeIndex: map };
  }, [source]);

  const [selected, setSelected] = useState<number | null>(null);

  const body = (
    <MarkdownRepoContext.Provider value={repo}>
      <MarkdownDirContext.Provider value={dirOf(path ?? "")}>
        {renderChildren(tree.children)}
      </MarkdownDirContext.Provider>
    </MarkdownRepoContext.Provider>
  );
  if (!onSourceChange) return body;

  return (
    <EmbedContext.Provider
      value={{
        interactive: true,
        source,
        onSourceChange,
        selected,
        setSelected,
        nodeIndex,
        repo,
        path: path ?? "",
        onRequestEdit,
      }}
    >
      {body}
    </EmbedContext.Provider>
  );
}
